import React from "react";
import { NavLink, Link } from "react-router-dom";
import {
  LayoutDashboard,
  Music4,
  Headphones,
  Wrench,
  ShoppingBag,
  Radio,
  GraduationCap,
  MessageSquare,
  Bell,
  UserCircle2,
  Settings,
  ShieldCheck,
  UserPlus,
  Users as UsersIcon,
  BarChart3,
  CreditCard,
  FileText,
  ShieldAlert,
  Globe2,
  HardDrive,
  Star,
  BadgePercent,
  Disc3,
  Sparkles,
  ServerCog,
} from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import { TID } from "@/lib/testIds";
import { cn } from "@/lib/utils";

const ITEMS = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, key: "dashboard" },
  { to: "/app/songwriters", label: "Songwriters", icon: Music4, key: "songwriters" },
  { to: "/app/music-industry", label: "Music Industry", icon: Headphones, key: "music-industry" },
  { to: "/app/services", label: "Services", icon: Wrench, key: "services" },
  { to: "/app/merchandising", label: "Merchandising", icon: ShoppingBag, key: "merchandising" },
  { to: "/app/radio", label: "Radio", icon: Radio, key: "radio" },
  { to: "/app/workshops", label: "Workshops", icon: GraduationCap, key: "workshops" },
  { to: "/app/messages", label: "Messages", icon: MessageSquare, key: "messages" },
  { to: "/app/notifications", label: "Notifications", icon: Bell, key: "notifications" },
  { to: "/app/profile/view", label: "Profile", icon: UserCircle2, key: "profile" },
  { to: "/app/settings", label: "Settings", icon: Settings, key: "settings" },
];

const ADMIN_ITEMS = [
  { to: "/app/admin", label: "Admin", icon: ShieldCheck, key: "admin" },
  { to: "/app/admin/analytics", label: "Analytics", icon: BarChart3, key: "admin-analytics" },
  { to: "/app/admin/waitlist", label: "Waitlist", icon: UserPlus, key: "admin-waitlist" },
  { to: "/app/admin/users", label: "Users", icon: UsersIcon, key: "admin-users" },
  { to: "/app/admin/ratings", label: "Ratings", icon: Star, key: "admin-ratings" },
  { to: "/app/admin/waivers", label: "Waivers & Discounts", icon: BadgePercent, key: "admin-waivers" },
  { to: "/app/admin/songs", label: "Songs & Radio", icon: Disc3, key: "admin-songs" },
  { to: "/app/admin/sip", label: "SIP Admin", icon: Sparkles, key: "admin-sip" },
  { to: "/app/admin/system", label: "System & Security", icon: ServerCog, key: "admin-system" },
  { to: "/app/admin/billing", label: "Billing", icon: CreditCard, key: "admin-billing" },
  { to: "/app/admin/reports", label: "Reports", icon: FileText, key: "admin-reports" },
  { to: "/app/admin/moderation", label: "Moderation", icon: ShieldAlert, key: "admin-moderation" },
  { to: "/app/admin/api", label: "API Keys", icon: Globe2, key: "admin-api" },
  { to: "/app/admin/storage", label: "Storage", icon: HardDrive, key: "admin-storage" },
];

export default function Sidebar() {
  const { isAdmin } = useAuth();

  return (
    <aside
      data-testid={TID.shell.sidebar}
      className="fixed inset-y-0 left-0 z-40 hidden md:flex md:w-64 flex-col border-r border-white/10 bg-matte"
    >
      <Link to="/" className="h-16 flex items-center px-6 border-b border-white/10">
        <Logo variant="inline" size={30} />
      </Link>
      <nav className="flex-1 overflow-y-auto py-6 px-3 space-y-1">
        {ITEMS.map((item) => (
          <NavItem key={item.key} item={item} />
        ))}
        {isAdmin && (
          <>
            <div className="mt-6 mb-2 px-3 text-[10px] uppercase tracking-[0.35em] text-white/40 font-display">Administration</div>
            {ADMIN_ITEMS.map((item) => (
              <NavItem key={item.key} item={item} end={item.to === "/app/admin"} />
            ))}
          </>
        )}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="text-[10px] uppercase tracking-[0.35em] text-white/40 font-display">
          Songwriter Square
        </div>
        <div className="text-[10px] text-white/30 mt-1">v1.0 · Phase 2</div>
      </div>
    </aside>
  );
}

function NavItem({ item, end = false }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={end}
      data-testid={`${TID.shell.navPrefix}${item.key}`}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 px-3 py-2.5 text-sm tracking-wider uppercase font-display transition-all border-l-2",
          isActive
            ? "text-gold border-gold bg-gold/5"
            : "text-white/60 border-transparent hover:text-white hover:bg-white/[0.03] hover:border-white/20",
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}
